import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Heart } from "lucide-react";

/**
 * FriendsWhoFavorited — social-proof row on the business profile.
 *
 * For signed-in customers only. Fetches the caller's friends who have
 * favorited THIS business and shows up to 8 avatars in an overlapping row,
 * followed by a plain-english summary (Alice, Bob and 3 others favorited
 * this business). Renders nothing when the customer has no friend-favorites
 * — no empty state to keep the profile page uncluttered.
 */
function Avatar({ url, name, size = 32 }) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className="rounded-full object-cover border-2 border-white shadow-sm"
        style={{ width: size, height: size }}
      />
    );
  }
  const initials = (name || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className="rounded-full bg-[#B31E1E]/10 text-[#B31E1E] font-medium flex items-center justify-center border-2 border-white shadow-sm"
      style={{ width: size, height: size, fontSize: size / 2.8 }}
    >
      {initials}
    </div>
  );
}

export default function FriendsWhoFavorited({ businessId }) {
  const { user } = useAuth();
  const [state, setState] = useState({ friends: [], friend_favorite_count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!user || user.role !== "customer") {
      setLoading(false);
      return () => {};
    }
    (async () => {
      try {
        const { data } = await api.get(`/businesses/${businessId}/friend-favorites`);
        if (!cancelled) setState(data || { friends: [], friend_favorite_count: 0 });
      } catch {
        // silent — social proof is best-effort
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, user]);

  // Hide entirely for non-customers, while loading, or when no friend has favorited
  if (loading || !user || user.role !== "customer" || state.friend_favorite_count === 0) {
    return null;
  }

  const { friends, friend_favorite_count: total } = state;
  const displayed = friends.slice(0, 4);
  const summary = (() => {
    if (total === 1) return `${friends[0].name} favorited this business`;
    if (total === 2) return `${friends[0].name} and ${friends[1].name} favorited this`;
    if (total === 3) return `${friends[0].name}, ${friends[1].name} and 1 other friend favorited this`;
    return `${friends[0].name}, ${friends[1].name} and ${total - 2} others favorited this`;
  })();

  return (
    <div
      className="mt-4 flex items-center gap-3 rounded-md border border-rose-100 bg-rose-50/60 px-3 py-2.5"
      data-testid="friends-who-favorited"
    >
      <Heart size={14} className="text-rose-500 shrink-0" fill="currentColor" />
      <div className="flex -space-x-2" data-testid="friends-who-favorited-avatars">
        {displayed.map((f) => (
          <Avatar key={f.id} url={f.avatar_data_url} name={f.name} />
        ))}
        {total > 4 && (
          <div
            className="rounded-full bg-white text-gray-600 text-[10px] font-mono flex items-center justify-center border-2 border-white shadow-sm"
            style={{ width: 32, height: 32 }}
          >
            +{total - 4}
          </div>
        )}
      </div>
      <span className="text-sm text-gray-700 leading-tight">{summary}</span>
    </div>
  );
}
